import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type SetStateAction,
} from 'react';

/**
 * Sorts all rows for which drag and drop should be disabled by default to either the start or end of the DataTable.
 *
 * @param props - The object containing the initial row order, the set of rows for which
 *                drag and drop should be disabled, and the position to which disabled rows should be
 *                sorted and locked.
 * @returns - An object containing the set of rows for which drag and drop should be disabled, the adapted row order
 *            with disabled rows sorted to the start or end of the table, and the function to update the row order
 *            on drag and drop.
 * @public
 */
export function useLockedRowOrder<TData = unknown>(props: {
  /**
   * Ordered set of row ids representing the initial row order.
   */
  initialRowOrder: string[];
  /**
   * Ids of the rows for which drag and drop is disabled and which are grouped and locked at the `position`.
   */
  lockedRows: string[];
  /**
   * Where the locked rows are grouped: `'start'` for the top of the table, `'end'` for the bottom.
   * @defaultValue 'start'
   */
  position?: 'start' | 'end';
  /**
   * Optional tooltip shown when a locked row's drag handle is disabled. Pass a string to
   * use the same tooltip for every locked row, or a function that receives the row data and
   * returns a per-row message (or `undefined` for no tooltip).
   *
   * @remarks If passing a function, ensure it is stable across renders (e.g. wrap it with
   * `useCallback` or define it outside the component) — an unstable reference triggers
   * unnecessary re-renders.
   */
  disabledRowDragAndDropTooltip?: string | ((row: TData) => string | undefined);
}) {
  const {
    initialRowOrder,
    lockedRows,
    position = 'start',
    disabledRowDragAndDropTooltip,
  } = props;

  const getOrderedRows = useCallback(
    (rows: string[]) => {
      if (!position) {
        return rows;
      }
      const { locked, unlocked } = rows.reduce<{
        locked: string[];
        unlocked: string[];
      }>(
        (acc, row) => {
          lockedRows.includes(row)
            ? acc.locked.push(row)
            : acc.unlocked.push(row);
          return acc;
        },
        { locked: [], unlocked: [] },
      );

      return position === 'start'
        ? [...locked, ...unlocked]
        : [...unlocked, ...locked];
    },
    [lockedRows, position],
  );

  const [rowOrder, setRowOrderState] = useState(
    getOrderedRows(initialRowOrder),
  );

  const setRowOrder = useCallback(
    (updater: SetStateAction<string[]>) => setRowOrderState(updater),
    [],
  );

  // If the position or locked rows change, update the row order
  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    setRowOrder(getOrderedRows(rowOrder));

    // Only needs to run when the specified position or the locked rows change
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedRows, position]);

  const disableRowDragAndDrop = useCallback(
    // oxlint-disable-next-line typescript/no-explicit-any
    (row: any) => lockedRows.includes(row.id),
    [lockedRows],
  );

  const rowOrdering = useMemo(
    () => ({
      enableDragAndDrop: true,
      disableRowDragAndDrop,
      lockDisabledRows: position,
      disabledRowDragAndDropTooltip,
    }),
    [disableRowDragAndDrop, position, disabledRowDragAndDropTooltip],
  );

  return {
    rowOrdering,
    rowOrder,
    setRowOrder,
  };
}
